import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { PlayerEvent } from '@lottiefiles/react-lottie-player';
import * as CreateCampaignDialog from 'components/create-campaign-dialog';
import {
    ChecklistButterStick,
    FloatingChecklistMrButter,
} from 'components/required-checklist/ChecklistMrButter';
import { FloatingChecklistListItemButton } from 'components/required-checklist/FloatingChecklistListItemButton';
import { RequiredChecklistGenericConfetti } from 'components/required-checklist/RequiredChecklistGenericConfetti';
import { RequiredChecklistProgressBar } from 'components/required-checklist/RequiredChecklistProgressBar';
import { ConnectAccountDialog } from 'components/required-checklist/required-items/ConnectAccountDialog';
import {
    useChecklistData,
    useCompletedAtLeastOneChecklistItem,
    useInvalidateChecklistQuery,
} from 'components/required-checklist/useChecklistData';
import { useCopy } from 'components/snippet';
import { posthogEvents } from 'constants/posthog-events';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { usePosthog } from 'customHooks/usePosthog';
import { useRequiredChecklistInit } from 'customHooks/useRequiredChecklistInit';
import { AnimatePresence, motion } from 'framer-motion';
import { getGenericPathFromPath } from 'helpers/formatting';

const RequiredFloatingChecklist = () => {
    const { pushEvent } = usePosthog();
    const { success } = useToast();
    const [open, setOpen] = useState(false);
    const { recentCampaigns, accountId } = useCurrentAccount();
    const [checklistAnimating, setChecklistAnimating] = useState(false);
    const [seenFireworks, setSeenFireworks] = useState(false);
    const { invalidateChecklistQuery } = useInvalidateChecklistQuery();
    const { checklist, completedAllActions } = useChecklistData();
    const { verifyEmail, createCampaign, connectPayoutAccount, raiseYourFirstDollar } = checklist;
    const completedAtLeastOne = useCompletedAtLeastOneChecklistItem();

    useRequiredChecklistInit();

    const location = useLocation();

    const { onClickCopy } = useCopy({
        onCopy: () => success('Campaign link copied!'),
    });

    const hasCreatedCampaign = recentCampaigns.length > 0;

    const logChecklistClick = useCallback(
        (checklistItemKey: string) => {
            pushEvent(posthogEvents.FLOATING_CHECKLIST_CHECKLIST_ITEM_SELECTED, {
                checklistItem: checklistItemKey,
            });
        },
        [pushEvent],
    );

    useEffect(() => {
        if (open) {
            invalidateChecklistQuery();
        }
    }, [open, invalidateChecklistQuery]);

    const onHomepage = useMemo(() => {
        const genericPath = getGenericPathFromPath(location.pathname);
        return genericPath === 'home' || !genericPath || genericPath.length == 0;
    }, [location.pathname]);

    if (onHomepage) {
        return null;
    }

    return (
        <div className="z-layerSeven pointer-events-none fixed bottom-0 right-2 h-80 w-80 sm:right-6">
            <div className="pointer-events-auto absolute -right-1 bottom-0">
                {!seenFireworks && (
                    <ChecklistButterStick
                        keepGoing={completedAtLeastOne}
                        onClick={() => {
                            pushEvent(posthogEvents.FLOATING_CHECKLIST_VIEWED, {
                                pagePath: getGenericPathFromPath(location.pathname),
                                checklistItems: Object.keys(checklist),
                            });
                            setOpen(true);
                        }}
                    />
                )}
            </div>
            <AnimatePresence>
                {open && (
                    <motion.div
                        className="absolute bottom-6 w-full"
                        initial={{ y: '120%' }}
                        animate={{ y: '0%' }}
                        exit={{
                            y: '120%',
                            transition: { ease: 'easeIn', duration: 0.3 },
                        }}
                        transition={{ ease: 'easeOut', duration: 0.45 }}
                        onAnimationStart={() => {
                            setChecklistAnimating(true);
                        }}
                        onAnimationComplete={() => {
                            setChecklistAnimating(false);
                        }}
                    >
                        <div className="pointer-events-auto flex h-full w-full flex-col rounded-xl bg-white shadow-lg outline outline-1 outline-gray-300">
                            <div className="flex w-full flex-col p-4">
                                <p className="text-sm font-medium">Let's get you set up!</p>
                                <RequiredChecklistProgressBar />
                            </div>
                            <ul className="flex h-full flex-col">
                                <Separator />
                                <Link to={`/accounts/${accountId}/settings/about/verify`}>
                                    <FloatingChecklistListItemButton
                                        isComplete={verifyEmail}
                                        onClick={() => logChecklistClick('verifyEmail')}
                                    >
                                        Verify email
                                    </FloatingChecklistListItemButton>
                                </Link>
                                <Separator />
                                <CreateCampaignDialog.Root
                                    triggerLocation={
                                        CreateCampaignDialog.TriggerLocation
                                            .RequiredChecklistFloating
                                    }
                                >
                                    <CreateCampaignDialog.Trigger asChild>
                                        <FloatingChecklistListItemButton
                                            isComplete={createCampaign}
                                            onClick={() => logChecklistClick('createCampaign')}
                                        >
                                            Create a campaign
                                        </FloatingChecklistListItemButton>
                                    </CreateCampaignDialog.Trigger>
                                </CreateCampaignDialog.Root>
                                <Separator />
                                <ConnectAccountDialog
                                    onOpenChange={open => {
                                        if (!open) {
                                            invalidateChecklistQuery();
                                        }
                                    }}
                                >
                                    <FloatingChecklistListItemButton
                                        isComplete={connectPayoutAccount}
                                        onClick={() => logChecklistClick('connectPayoutAccount')}
                                    >
                                        Connect payout method
                                    </FloatingChecklistListItemButton>
                                </ConnectAccountDialog>
                                <Separator />
                                <FloatingChecklistListItemButton
                                    isComplete={raiseYourFirstDollar}
                                    disabled={!hasCreatedCampaign || !connectPayoutAccount}
                                    onClick={e => {
                                        onClickCopy(e, recentCampaigns[0].url);
                                        logChecklistClick('raiseYourFirstDollar');
                                    }}
                                >
                                    Raise your first dollar
                                </FloatingChecklistListItemButton>
                                <Separator />
                                <div className="flex h-full items-center justify-end p-2">
                                    <Button
                                        onClick={() => setOpen(false)}
                                        variant="transparent"
                                        color="neutral"
                                        className="text-sm hover:bg-gray-50"
                                    >
                                        Minimize
                                    </Button>
                                </div>
                            </ul>

                            <div className="absolute -top-10 right-4">
                                <FloatingChecklistMrButter
                                    jumping={open && checklistAnimating}
                                    keepGoing={completedAtLeastOne}
                                />
                            </div>

                            {completedAllActions && (
                                <RequiredChecklistGenericConfetti
                                    onEvent={event => {
                                        if (event === PlayerEvent.Complete) {
                                            setSeenFireworks(true);
                                            setOpen(false);
                                        }
                                    }}
                                />
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export { RequiredFloatingChecklist };
